import { Stack, Typography } from '@hiiretail/react-foundation-ui';
import { Box, Select, Switch, TextField } from '@hiiretail/synergy-ui';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { InlineHint } from '../../../../../../common/components/InlineHint/InlineHint';
import { OidcAuthFlow } from '../../../../../../api-client';

type OidcFlowConfigFormProps = {
  control: UseFormReturn['control'];
  formState: UseFormReturn['formState'];
  selectedFlow: OidcAuthFlow;
  idpFlow?: 'create' | 'update';
};

export const OidcFlowConfigForm: React.FC<OidcFlowConfigFormProps> = ({
  control,
  formState,
  selectedFlow,
  idpFlow,
}) => {
  return (
    <Stack
      sx={{
        padding: 2,
        border: '1px solid rgba(211, 211, 211, 0.48)',
        borderRadius: '4px',
      }}
      spacing={2}
      data-testid="oidc-flow-config-form-wrapper"
    >
      <Box>
        <Typography fontWeight="bold" variant="body1">
          Authentication Flow & Security Settings
          <Typography variant="caption" fontWeight="regular" color="gray" display="block">
            Choose how your app authenticates users and enhance security with PKCE if needed.
          </Typography>
        </Typography>
      </Box>

      <Box mt={3} data-testid="oidc-flow-config-form">
        <Box mb={3}>
          <Controller
            name="flow"
            control={control}
            defaultValue={OidcAuthFlow.Implicit}
            render={({ field: { value, onChange } }) => (
              <Select
                fullWidth
                label="Flow"
                name="flow"
                value={{ label: value, value: value }}
                isSearchable={false}
                isClearable={false}
                placeholder={value}
                onChange={(option: Option | null) => onChange(option?.value)}
                error={formState.errors?.flow?.message as string | undefined}
                options={[
                  { label: 'Implicit', value: OidcAuthFlow.Implicit },
                  { label: 'Code', value: OidcAuthFlow.Code },
                ]}
              />
            )}
          />
        </Box>

        {selectedFlow === OidcAuthFlow.Code && (
          <>
            <Box mb={2}>
              <Controller
                control={control}
                defaultValue={false}
                name="usePKCE"
                render={({ field: { value, onChange } }) => (
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold">
                      Enable PKCE
                      <Typography variant="caption" color="text.secondary" display="block">
                        Enable Proof Key for Code Exchange
                      </Typography>
                    </Typography>
                    <Switch onChange={onChange} name="usePKCE" checked={value ?? false} />
                  </Box>
                )}
              />
            </Box>

            <Box mb={2}>
              <Controller
                name="clientSecret"
                control={control}
                render={({ field: { ref, value, onChange } }) => (
                  <TextField
                    tabIndex={0}
                    fullWidth
                    label="Client Secret"
                    name="clientSecret"
                    type="password"
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    placeholder="•••••••"
                    required={idpFlow === 'create'}
                    trailingIcon={
                      <InlineHint>
                        <Typography variant="body2" fontWeight="bold">
                          Client Secret
                        </Typography>
                        <Typography variant="caption">
                          Used to authenticate your application with the authorization server. Keep
                          this value confidential.
                          <br /> We don’t store it, so you won’t be able to view it again.
                        </Typography>
                      </InlineHint>
                    }
                    error={formState.errors?.clientSecret?.message as string | undefined}
                  />
                )}
              />
            </Box>
          </>
        )}
      </Box>
    </Stack>
  );
};
